import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  selectCurrentDocument,
  selectCurrentProject,
} from '../../../../redux/application-slice'
import {
  selectShowClauseReferences,
  selectCurrentPage,
  selectTotalPages,
  setCurrentPage,
} from '../../../../redux/viewer-slice'
import {
  useGetClauseReferencesQuery,
  useGetDocumentsListByProjectQuery,
  useGetUserProfileQuery,
} from '../../../../redux/api-slice'
import { useBackButton } from '../../../../hooks/use-back-button'
import { useWorkflow } from '../../../../hooks/use-workflow'
import UseUrlNavigate from '../../../../hooks/use-url-navigate'
import {
  CrossReferenceHighlight,
  SelectedReference,
} from '../../../../shared/interfaces/clause-references/cross-reference.interface'
import { Quads } from '../../../../shared/interfaces/quads.interface'
import { isRatio } from '../../../../utils/convert-quads'
import {
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { skipToken } from '@reduxjs/toolkit/query'

declare global {
  interface Window {
    referenceClickHandler?: (
      x: number,
      y: number,
      label: string,
      page: number
    ) => void
  }
}

export const DocumentViewerCrossReference: React.FC = () => {
  const { workflowMatch } = useWorkflow()
  const { addToNavigationHistoryAndNavigate } = useBackButton()
  const urlNavigate = UseUrlNavigate()
  const dispatch = useDispatch()

  const showClauseRefs = useSelector(selectShowClauseReferences)
  const currentDocument = useSelector(selectCurrentDocument)
  const currentProject = useSelector(selectCurrentProject)
  const currentPage = useSelector(selectCurrentPage)
  const totalPages = useSelector(selectTotalPages)
  const { data: user } = useGetUserProfileQuery(undefined)

  const [selectedRef, setSelectedRef] = useState<SelectedReference | null>(null)
  const previousDocumentUuid = useRef<string | null>(null)

  const { data: matches = [], error } = useGetClauseReferencesQuery(
    {
      projectId: currentProject?.uuid ?? '',
      documentId: currentDocument?.uuid ?? '',
    },
    {
      skip:
        !showClauseRefs ||
        !currentDocument?.uuid ||
        !currentProject?.uuid ||
        !user?.feature_flags?.section_reference,
    }
  )

  const { data: allDocuments = [] } = useGetDocumentsListByProjectQuery(
    currentProject ? { projectId: currentProject?.id } : skipToken
  )

  useEffect(() => {
    const clear = () => setSelectedRef(null)
    document.addEventListener('click', clear)
    return () => document.removeEventListener('click', clear)
  }, [])

  // Handle page reset when switching to a document with fewer pages
  useEffect(() => {
    const currentDocUuid = currentDocument?.uuid ?? null
    const hasDocumentChanged = previousDocumentUuid.current !== currentDocUuid

    if (hasDocumentChanged && totalPages > 0 && currentPage > totalPages) {
      // Current page is beyond the document's page count, reset to page 1
      dispatch(setCurrentPage(1))
    }

    previousDocumentUuid.current = currentDocUuid
  }, [currentDocument?.uuid, totalPages, currentPage, dispatch])

  useEffect(() => {
    setSelectedRef(null)
    window.referenceClickHandler = undefined
  }, [currentDocument?.uuid, currentProject?.uuid])

  useEffect(() => {
    if (!showClauseRefs || matches.length === 0) {
      setSelectedRef(null)
      window.referenceClickHandler = undefined
      return
    }

    const highlights: Record<string, CrossReferenceHighlight[]> = {}
    matches.forEach((m) => {
      const key = `${m.section_label}::${m.segment.page}`
      const quads: Quads[] = m.segment.quads.map((q) => ({
        ...q,
        page: m.segment.page,
      }))
      const h: CrossReferenceHighlight = {
        page: m.segment.page,
        quads,
        id: '',
        pdfCoords: isRatio(quads),
        colourClass: '',
        contextText: m.context_text,
        sectionLabel: m.section_label,
        referencedPage: m.page_of_heading,
        segmentId: m.segment.id,
        referencedDocumentId: m.document_of_heading,
      }
      highlights[key] = (highlights[key] || []).concat(h)
    })

    window.referenceClickHandler = (x, y, label, page) => {
      const key = `${label}::${page}`
      const arr = highlights[key]
      if (!arr?.length) return

      // Find the referenced document by document_of_heading
      const referencedDocument = allDocuments.find(
        (doc) => doc.id === arr[0].referencedDocumentId
      )

      // If we can't find the referenced document, it might be the current document
      const finalReferencedDocumentUuid =
        referencedDocument?.uuid ?? currentDocument?.uuid
      const finalReferencedDocumentTitle =
        referencedDocument?.title ?? currentDocument?.title

      setSelectedRef({
        label,
        context: Array.from(new Set(arr.map((m) => m.contextText))).join(
          '\n\n'
        ),
        segmentId: arr[0].segmentId,
        page,
        referencedPage: arr[0].referencedPage,
        coords: { x, y },
        documentTitle: currentDocument?.title,
        documentUuid: currentDocument?.uuid,
        referencedDocumentId: arr[0].referencedDocumentId,
        referencedDocumentUuid: finalReferencedDocumentUuid,
        referencedDocumentTitle: finalReferencedDocumentTitle,
      })
    }
  }, [
    showClauseRefs,
    matches,
    currentDocument?.title,
    currentDocument?.uuid,
    allDocuments,
  ])

  const handleNavigate = useCallback(() => {
    if (!selectedRef || !workflowMatch.route) return

    // Close the popup when navigating
    setSelectedRef(null)

    // Check if we need to navigate to a different document
    const targetDocumentUuid = selectedRef.referencedDocumentUuid
    const isCurrentDocument = targetDocumentUuid === currentDocument?.uuid

    if (isCurrentDocument) {
      // Same document navigation
      addToNavigationHistoryAndNavigate(
        workflowMatch.route,
        currentDocument?.uuid ?? '',
        selectedRef.referencedPage
      )
    } else if (targetDocumentUuid) {
      // Cross-document navigation
      urlNavigate.navigateDocumentUrl(targetDocumentUuid, {
        page: selectedRef.referencedPage,
      })
    }
  }, [
    selectedRef,
    addToNavigationHistoryAndNavigate,
    currentDocument?.uuid,
    workflowMatch.route,
    urlNavigate,
  ])

  const handleClose = useCallback(() => {
    setSelectedRef(null)
  }, [])

  if (!showClauseRefs || error || !user?.feature_flags?.section_reference)
    return null

  return (
    <div className="pointer-events-auto">
      {selectedRef && (
        <div
          className="fixed z-50 w-96 rounded-xl border border-gray-200 bg-white shadow-xl"
          style={{
            left: Math.min(selectedRef.coords.x + 20, window.innerWidth - 400),
            top: Math.min(selectedRef.coords.y + 20, window.innerHeight - 320),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between border-b border-gray-100 p-4 pb-3">
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center space-x-2">
                <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700">
                  Reference
                </span>
                <span className="text-xs text-gray-500">
                  Page {selectedRef.referencedPage}
                </span>
              </div>
              <h3 className="truncate text-sm font-semibold text-gray-900">
                {selectedRef.label}
              </h3>
              {selectedRef.referencedDocumentTitle && (
                <p className="mt-1 truncate text-xs text-gray-500">
                  {selectedRef.referencedDocumentTitle}
                </p>
              )}
            </div>
            <button
              onClick={handleClose}
              className="ml-2 flex-shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            <div className="max-h-32 overflow-y-auto">
              <p className="whitespace-pre-wrap text-xs text-gray-700">
                {selectedRef.context}
              </p>
            </div>
          </div>

          <div className="border-t border-gray-100 px-4 py-3">
            <button
              className="inline-flex items-center space-x-2 text-xs font-medium text-indigo-600 transition-colors hover:text-indigo-800"
              onClick={handleNavigate}
            >
              <span>Go to page {selectedRef.referencedPage}</span>
              <ArrowTopRightOnSquareIcon className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default DocumentViewerCrossReference
